<template>
  <div class="project-container">
    <!-- Hero Section -->
    <header class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">SAE - Projet Académique</div>
        <h1 class="hero-title">Referendum</h1>
        <p class="hero-subtitle">Application de vote électronique sécurisée</p>
        <div class="hero-meta">
          <span class="meta-item"><i class="mdi mdi-calendar"></i> Octobre 2024 - 6 mois</span>
          <span class="meta-item"><i class="mdi mdi-account-group"></i> Travail d'équipe</span>
          <span class="meta-item"><i class="mdi mdi-security"></i> Cryptographie avancée</span>
        </div>
      </div>
    </header>

    <!-- Overview Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-information-outline"></i>
        <h2>Vue d'ensemble</h2>
      </div>
      <div class="card-content">
        <p class="lead-text">
          <strong>Referendum</strong> est une application <strong>client-serveur</strong> complète permettant
          l'organisation de référendums en ligne avec une garantie absolue de <strong>confidentialité</strong>
          et de <strong>sécurité</strong> des votes. Ce projet académique d'envergure a été réalisé en
          <strong>méthodologie Agile</strong> sur une période de <strong>6 mois</strong>, avec des sprints
          réguliers et des livraisons progressives de fonctionnalités.
        </p>
        <p>
          Le projet s'inscrit dans le cadre d'une SAE (Situation d'Apprentissage et d'Évaluation) où nous
          devions concevoir et développer une solution de vote électronique répondant aux exigences les plus
          strictes en matière de sécurité informatique. L'objectif principal était de garantir que chaque vote
          reste <strong>anonyme</strong> et <strong>infalsifiable</strong>, tout en permettant une vérification
          de l'intégrité du processus de dépouillement. Cette dualité entre anonymat et traçabilité constitue
          le défi technique majeur du projet, résolu grâce à l'utilisation de techniques cryptographiques avancées.
        </p>

        <div class="tech-stack">
          <span class="tech-badge">Java</span>
          <span class="tech-badge">Sockets</span>
          <span class="tech-badge">ElGamal</span>
          <span class="tech-badge">TLS</span>
          <span class="tech-badge">Vue.js</span>
          <span class="tech-badge">Gradle</span>
          <span class="tech-badge">GitLab</span>
        </div>
      </div>
    </section>

    <!-- Architecture Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-sitemap"></i>
        <h2>Architecture Technique</h2>
      </div>
      <div class="card-content">
        <h3 class="section-subtitle">Architecture Monolithe Modulaire</h3>
        <p>
          L'architecture du système a été conçue selon un modèle <strong>monolithe modulaire</strong>, offrant
          un équilibre optimal entre simplicité de déploiement et séparation des responsabilités. Chaque module
          est indépendant et communique via des interfaces bien définies, facilitant la maintenance et les
          évolutions futures. Cette approche nous a permis de gérer efficacement la complexité tout en conservant
          une base de code cohérente.
        </p>
        <p>
          Le système est composé de trois entités principales : les <strong>électeurs</strong> qui soumettent leurs
          votes via une interface sécurisée, le <strong>serveur central</strong> qui orchestre les opérations et
          garantit l'intégrité des données, et les <strong>scrutateurs</strong> qui gèrent le processus de dépouillement
          sans jamais avoir accès aux votes en clair. L'ensemble communique via un protocole de trames personnalisé
          transporté sur des sockets sécurisées par TLS.
        </p>

        <div class="feature-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-network"></i>
            </div>
            <h4>Communication Sockets</h4>
            <p>Mise en place d'une communication en temps réel entre toutes les entités du système
            (électeurs, serveur, scrutateurs) via des sockets Java. Cette architecture permet des échanges
            bidirectionnels instantanés et garantit une synchronisation parfaite des états entre les différents
            participants du processus de vote.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-lock-outline"></i>
            </div>
            <h4>Chiffrement TLS</h4>
            <p>Sécurisation complète de toutes les communications réseau grâce au protocole TLS avec gestion
            de certificats. Cette couche de protection cryptographique garantit la confidentialité des échanges
            et empêche toute interception ou modification malveillante des données en transit.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-file-document-outline"></i>
            </div>
            <h4>Protocole de Trames</h4>
            <p>Conception d'un protocole de communication personnalisé basé sur un système de trames structurées.
            Chaque message transporté contient des métadonnées standardisées (type, statut, timestamp) et une
            charge utile, permettant une communication claire et robuste entre les composants.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-puzzle-outline"></i>
            </div>
            <h4>Modules Gradle</h4>
            <p>Organisation du projet en modules Gradle indépendants, chacun ayant ses propres dépendances et
            responsabilités. Cette structure modulaire facilite grandement la maintenance, les tests unitaires,
            et permet une compilation sélective pour optimiser les temps de build.</p>
          </div>
        </div>

        <div class="image-showcase">
          <img :src="require('@/assets/image/archi-projet.png')" alt="Architecture du projet"/>
          <span class="image-caption">Schéma d'architecture modulaire</span>
        </div>
      </div>
    </section>

    <!-- Crypto Card -->
    <section class="card crypto-card">
      <div class="card-header">
        <i class="mdi mdi-shield-key"></i>
        <h2>Cryptographie</h2>
      </div>
      <div class="card-content">
        <h3 class="section-subtitle">Sécurisation des Votes</h3>
        <p>
          J'ai été responsable de <strong>toute la partie cryptographie</strong> du projet, qui constitue
          la pierre angulaire du système en garantissant simultanément la <strong>confidentialité</strong>
          et la <strong>vérifiabilité</strong> des votes. Cette responsabilité m'a amené à étudier en profondeur
          les algorithmes cryptographiques modernes et à les adapter aux contraintes spécifiques du vote électronique.
        </p>
        <p>
          Le système repose sur une implémentation sophistiquée de l'algorithme <strong>ElGamal modifié</strong>,
          qui offre des propriétés de chiffrement homomorphe essentielles pour notre cas d'usage. Cette caractéristique
          permet d'effectuer le dépouillement directement sur les votes chiffrés, sans jamais avoir à les déchiffrer
          individuellement, préservant ainsi l'anonymat absolu de chaque électeur tout en garantissant l'exactitude
          du résultat final.
        </p>

        <div class="crypto-features">
          <div class="crypto-item">
            <div class="crypto-icon">
              <i class="mdi mdi-key-variant"></i>
            </div>
            <div class="crypto-content">
              <h4>Algorithme ElGamal Modifié</h4>
              <p>Implémentation complète et adaptation de l'algorithme ElGamal aux besoins spécifiques du système
              de vote. J'ai développé les fonctions de génération de clés, de chiffrement et de déchiffrement en
              respectant les propriétés mathématiques de l'algorithme, tout en optimisant les performances pour
              traiter un grand volume de votes en temps raisonnable.</p>
            </div>
          </div>

          <div class="crypto-item">
            <div class="crypto-icon">
              <i class="mdi mdi-function-variant"></i>
            </div>
            <div class="crypto-content">
              <h4>Chiffrement Homomorphe</h4>
              <p>Exploitation des propriétés homomorphes d'ElGamal permettant de réaliser des opérations
              arithmétiques directement sur les données chiffrées. Cette fonctionnalité cruciale autorise le
              dépouillement des votes sans jamais avoir à les déchiffrer individuellement, garantissant ainsi
              un anonymat absolu tout en permettant de calculer le résultat final de manière sécurisée et
              vérifiable.</p>
            </div>
          </div>

          <div class="crypto-item">
            <div class="crypto-icon">
              <i class="mdi mdi-check-circle-outline"></i>
            </div>
            <div class="crypto-content">
              <h4>Vérification d'Intégrité</h4>
              <p>Développement d'un système complet de preuves cryptographiques permettant à chaque participant
              de vérifier l'intégrité du processus. Les électeurs peuvent s'assurer que leur vote a bien été pris
              en compte, tandis que les scrutateurs peuvent prouver mathématiquement que le dépouillement est
              correct, le tout sans compromettre l'anonymat des votes.</p>
            </div>
          </div>

          <div class="crypto-item">
            <div class="crypto-icon">
              <i class="mdi mdi-certificate"></i>
            </div>
            <div class="crypto-content">
              <h4>Protocoles TLS</h4>
              <p>Mise en place d'une infrastructure à clés publiques (PKI) complète avec génération et gestion
              de certificats TLS pour sécuriser toutes les communications réseau entre les entités du système.
              Cette couche de sécurité transport garantit la confidentialité, l'authentification et l'intégrité
              de tous les échanges, empêchant toute attaque de type man-in-the-middle.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Admin Panel Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-monitor-dashboard"></i>
        <h2>Interface d'Administration</h2>
      </div>
      <div class="card-content">
        <p>
          L'interface d'administration constitue le centre de contrôle du système de vote, offrant aux
          administrateurs une visibilité complète sur l'état du système et des outils puissants pour gérer
          tous les aspects du référendum. Cette interface web moderne a été conçue pour être intuitive tout
          en donnant accès à des fonctionnalités avancées de supervision et de configuration.
        </p>
        <p>
          Le panneau permet notamment de gérer dynamiquement les permissions des utilisateurs, de suivre en
          temps réel l'état des connexions au serveur, et de superviser le déroulement du vote. L'interface
          affiche également des statistiques détaillées sur la participation, permettant aux administrateurs
          de monitorer efficacement le processus démocratique tout en respectant l'anonymat des votes.
        </p>

        <div class="admin-features">
          <div class="admin-feature">
            <i class="mdi mdi-account-multiple"></i>
            <span>Gestion des utilisateurs et scrutateurs</span>
          </div>
          <div class="admin-feature">
            <i class="mdi mdi-connection"></i>
            <span>Suivi des connexions actives</span>
          </div>
          <div class="admin-feature">
            <i class="mdi mdi-shield-account"></i>
            <span>Ajustement des permissions en temps réel</span>
          </div>
          <div class="admin-feature">
            <i class="mdi mdi-eye-check"></i>
            <span>Supervision du processus de vote</span>
          </div>
        </div>

        <div class="image-showcase">
          <img :src="require('@/assets/image/admin-user-panel.png')" alt="Panneau d'administration"/>
          <span class="image-caption">Interface administrateur</span>
        </div>
      </div>
    </section>

    <!-- Evolution Card -->
    <section class="card evolution-card">
      <div class="card-header">
        <i class="mdi mdi-rocket-launch"></i>
        <h2>Évolutions du Projet</h2>
      </div>
      <div class="card-content">
        <p>
          Au fil des <strong>6 mois de développement</strong>, le projet a connu plusieurs évolutions majeures
          motivées par nos apprentissages et les retours reçus lors des différents sprints Agile. Ces améliorations
          ont permis de transformer progressivement le projet d'une preuve de concept fonctionnelle vers une
          application robuste et professionnelle.
        </p>

        <div class="evolution-grid">
          <div class="evolution-item">
            <div class="evolution-header">
              <i class="mdi mdi-vuejs"></i>
              <h4>Refonte Front-end</h4>
            </div>
            <p>
              Migration complète de toutes les interfaces vers <strong>Vue.js 3</strong> pour remplacer les
              anciennes pages HTML/CSS statiques. Cette refonte a permis d'uniformiser l'expérience utilisateur
              sur toutes les pages du système, d'améliorer la réactivité de l'interface, et de faciliter
              grandement la maintenance grâce à une architecture basée sur des composants réutilisables. Le
              code front-end est devenu beaucoup plus modulaire et testable.
            </p>
          </div>

          <div class="evolution-item">
            <div class="evolution-header">
              <i class="mdi mdi-shield-check"></i>
              <h4>Preuves Cryptographiques</h4>
            </div>
            <p>
              Ajout de <strong>preuves cryptographiques avancées</strong> de type zero-knowledge pour renforcer
              la sécurité des échanges critiques. Ces preuves permettent aux scrutateurs de démontrer
              mathématiquement la validité de leurs opérations sans révéler d'informations sensibles, et aux
              électeurs de vérifier que leur vote a été correctement enregistré. Cette amélioration a
              considérablement renforcé la confiance dans le système en ajoutant une couche de transparence
              vérifiable.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Learning Card -->
    <section class="card learning-card">
      <div class="card-header">
        <i class="mdi mdi-lightbulb-on"></i>
        <h2>Compétences Développées</h2>
      </div>
      <div class="card-content">
        <div class="learning-grid">
          <div class="learning-category">
            <h4>Cryptographie</h4>
            <ul>
              <li>Implémentation ElGamal modifié</li>
              <li>Chiffrement homomorphe</li>
              <li>Preuves de connaissance</li>
              <li>Gestion de clés cryptographiques</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Développement Réseau</h4>
            <ul>
              <li>Programmation sockets Java</li>
              <li>Protocoles de communication</li>
              <li>Sécurisation TLS</li>
              <li>Gestion des erreurs réseau</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Architecture Logicielle</h4>
            <ul>
              <li>Monolithe modulaire avec Gradle</li>
              <li>Principes SOLID</li>
              <li>Séparation des responsabilités</li>
              <li>Code maintenable et évolutif</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Gestion de Projet</h4>
            <ul>
              <li>Méthodologie Agile (sprints)</li>
              <li>Collaboration GitLab</li>
              <li>Livraisons progressives</li>
              <li>Communication d'équipe</li>
            </ul>
          </div>
        </div>

        <div class="highlight-box">
          <i class="mdi mdi-star-four-points"></i>
          <p>
            Ce projet de <strong>6 mois</strong> m'a permis de maîtriser des concepts
            avancés de cryptographie tout en développant une application robuste et sécurisée.
            Une expérience formatrice combinant théorie mathématique et pratique du développement.
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';

@Component({})
export default class ReferendumComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'Inter', 'Segoe UI', sans-serif;
  line-height: 1.8;
  color: var(--text-color);
  background-color: var(--background-color);
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  padding-left: 100px;
}

/* Hero Section */
.hero-section {
  margin-bottom: 60px;
  padding: 60px 40px;
  background: linear-gradient(135deg, var(--card-background) 0%, var(--dark-bg-secondary) 100%);
  border-radius: 24px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.hero-content {
  display: flex;
  flex-direction: column;
}

.hero-badge {
  display: inline-block;
  width: fit-content;
  padding: 8px 16px;
  background: rgba(145, 174, 79, 0.2);
  color: #91ae4f;
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
  margin-bottom: 16px;
  border: 1px solid #91ae4f;
}

.hero-title {
  font-size: 4em;
  font-weight: 800;
  color: var(--h1-color);
  margin: 0 0 16px 0;
  background: linear-gradient(135deg, #ffffff 0%, var(--tertiary-color) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 1.6em;
  color: var(--sub-color);
  margin-bottom: 24px;
  font-weight: 400;
}

.hero-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
  font-size: 0.95em;
}

.meta-item i {
  color: var(--primary-color);
}

/* Card Styles */
.card {
  background: var(--card-background);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 32px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(19, 124, 139, 0.2);
  border-color: var(--primary-color);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--dark-border);
}

.card-header i {
  font-size: 32px;
  color: var(--primary-color);
}

.card-header h2 {
  font-size: 2em;
  font-weight: 700;
  color: var(--h2-color);
  margin: 0;
}

.card-content {
  color: var(--text-color);
}

.lead-text {
  font-size: 1.1em;
  line-height: 1.8;
  margin-bottom: 24px;
}

.section-subtitle {
  font-size: 1.4em;
  color: var(--h2-color);
  margin: 0 0 16px 0;
  font-weight: 600;
}

/* Tech Stack */
.tech-stack {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.tech-badge {
  padding: 10px 20px;
  background: rgba(19, 124, 139, 0.15);
  color: var(--primary-color);
  border-radius: 12px;
  font-size: 0.9em;
  font-weight: 600;
  border: 1px solid rgba(19, 124, 139, 0.3);
  transition: all 0.3s ease;
}

.tech-badge:hover {
  background: rgba(19, 124, 139, 0.25);
  transform: translateY(-2px);
}

/* Feature Grid */
.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin: 32px 0;
}

.feature-item {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  transition: all 0.3s ease;
  text-align: center;
}

.feature-item:hover {
  background: var(--dark-bg-card);
  border-color: var(--primary-color);
  transform: translateY(-4px);
}

.feature-icon {
  width: 60px;
  height: 60px;
  background: rgba(19, 124, 139, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.feature-icon i {
  font-size: 28px;
  color: var(--primary-color);
}

.feature-item h4 {
  font-size: 1.1em;
  color: var(--h2-color);
  margin: 0 0 8px 0;
  font-weight: 600;
}

.feature-item p {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.95em;
  line-height: 1.6;
}

/* Crypto Features */
.crypto-features {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 32px 0;
}

.crypto-item {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 20px;
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  border-left: 4px solid var(--primary-color);
  transition: all 0.3s ease;
}

.crypto-item:hover {
  background: var(--dark-bg-card);
  transform: translateX(8px);
}

.crypto-icon {
  width: 80px;
  height: 80px;
  background: rgba(19, 124, 139, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.crypto-icon i {
  font-size: 40px;
  color: var(--primary-color);
}

.crypto-content h4 {
  font-size: 1.3em;
  color: var(--h2-color);
  margin: 0 0 8px 0;
  font-weight: 600;
}

.crypto-content p {
  color: var(--text-muted);
  margin: 0;
  line-height: 1.6;
}

/* Admin Features */
.admin-features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  margin: 32px 0;
}

.admin-feature {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: var(--dark-bg-secondary);
  border-radius: 12px;
  border-left: 3px solid var(--primary-color);
}

.admin-feature i {
  font-size: 24px;
  color: var(--primary-color);
}

.admin-feature span {
  color: var(--text-color);
  font-size: 1em;
}

/* Evolution Grid */
.evolution-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
}

.evolution-item {
  padding: 32px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  transition: all 0.3s ease;
}

.evolution-item:hover {
  border-color: var(--primary-color);
  transform: translateY(-4px);
}

.evolution-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.evolution-header i {
  font-size: 32px;
  color: var(--primary-color);
}

.evolution-header h4 {
  font-size: 1.3em;
  color: var(--h2-color);
  margin: 0;
  font-weight: 600;
}

/* Image Showcase */
.image-showcase {
  margin: 32px 0;
  text-align: center;
}

.image-showcase img {
  max-width: 100%;
  height: auto;
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.image-caption {
  display: block;
  margin-top: 12px;
  color: var(--text-muted);
  font-size: 0.9em;
  font-style: italic;
}

/* Learning Grid */
.learning-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.learning-category {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
}

.learning-category h4 {
  font-size: 1.2em;
  color: var(--primary-color);
  margin: 0 0 16px 0;
  font-weight: 600;
}

.learning-category ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.learning-category li {
  padding: 8px 0 8px 24px;
  color: var(--text-color);
  position: relative;
  font-size: 0.95em;
}

.learning-category li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: var(--primary-color);
  font-weight: bold;
}

/* Highlight Box */
.highlight-box {
  padding: 32px;
  background: linear-gradient(135deg, rgba(19, 124, 139, 0.1) 0%, rgba(19, 124, 139, 0.05) 100%);
  border-radius: 16px;
  border: 2px solid var(--primary-color);
  display: flex;
  gap: 20px;
  align-items: center;
}

.highlight-box i {
  font-size: 48px;
  color: var(--primary-color);
  flex-shrink: 0;
}

.highlight-box p {
  margin: 0;
  font-size: 1.1em;
  line-height: 1.8;
}

/* Responsive */
@media (max-width: 768px) {
  .project-container {
    padding-left: 20px;
  }

  .hero-title {
    font-size: 2.5em;
  }

  .crypto-item, .evolution-item {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .crypto-icon {
    margin: 0 auto;
  }
}
</style>
